import { Component, type ErrorInfo, type ReactNode } from 'react';

import { SEMANTIC_STATE_TOKENS } from '../shared/tokens/index';

/**
 * The application error boundary.
 *
 * A caught render fault is a FRONTEND ENGINEERING DEFECT. It is not a read-model
 * state, and this boundary deliberately does not convert it into one:
 *
 *   - it does NOT render UNKNOWN, because UNKNOWN means the read plane resolved
 *     and the subject's data is absent — a fault says nothing about the subject;
 *   - it does NOT render UNAVAILABLE, because that means a functioning reader
 *     could not supply a trustworthy response — a fault is not a reader outcome;
 *   - it does NOT render EMPTY, because an empty projection is a fact about data;
 *   - it does NOT silently render children, which would present a partial tree as
 *     a complete one.
 *
 * It renders the ERROR condition, whose token text says plainly that no read
 * result is being reported.
 */

interface AppErrorBoundaryProps {
  readonly children: ReactNode;
}

interface AppErrorBoundaryState {
  readonly faulted: boolean;
}

export class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  override state: AppErrorBoundaryState = { faulted: false };

  static getDerivedStateFromError(): AppErrorBoundaryState {
    return { faulted: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('FE1-W0 frontend fault', error, info.componentStack);
  }

  override render(): ReactNode {
    if (!this.state.faulted) {
      return this.props.children;
    }

    const token = SEMANTIC_STATE_TOKENS.CONDITION_ERROR;

    return (
      <div role="alert" className="fe1w0-fault" data-tone={token.tone}>
        <p className="fe1w0-fault__label">{token.label}</p>
        <p className="fe1w0-fault__body">{token.description}</p>
        <p className="fe1w0-fault__note">
          This notice is not an unknown state and not a data absence. While it is shown, no read
          result is being reported for anything.
        </p>
      </div>
    );
  }
}
